import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { ErrorModal, getMomentTz } from "@dexteel/mesf-core";
import { Add } from "@mui/icons-material";
import { Button, Grid2, MenuItem, Paper, TextField } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { UTILITY_KIND } from "../../../shared/models/Utility";
import type { GasBillsData } from "../models/GasBilling";
import {
	deleteGasBill,
	getGasBills,
	upsertGasBill,
} from "../repositories/GasBillingRepository";
import { GasBillsTable } from "./GasBillsTable";
import { NewGasBillDialog } from "./NewGasBillDialog";

const moment = getMomentTz();

const billYears = () => {
	const currentYear = new Date().getFullYear();
	return Array.from({ length: 6 }, (_, index) => currentYear - index);
};

const GAS_BILL_QUERIES = [
	["gas-bills"],
	["gas-entries"],
	["gas-consumption"],
	["utility-consumption-report", UTILITY_KIND.GAS],
];

export const useGasBills = ({ year }: { year: number | null }) =>
	useQuery<GasBillsData, Error>({
		queryKey: ["gas-bills", year],
		queryFn: ({ signal }) =>
			getGasBills(
				{
					startDate: year ? moment().year(year).startOf("year").toDate() : null,
					endDate: year ? moment().year(year).endOf("year").toDate() : null,
				},
				signal,
			),
		staleTime: 60_000,
	});

export const GasBills = () => {
	const queryClient = useQueryClient();

	const [year, setYear] = useState<number | null>(new Date().getFullYear());
	const [isNewBillOpen, setIsNewBillOpen] = useState(false);
	const [billToDelete, setBillToDelete] = useState<number | null>(null);
	const [error, setError] = useState("");

	const { data, isFetching, isError, error: loadError } = useGasBills({ year });

	const invalidate = () =>
		Promise.all(
			GAS_BILL_QUERIES.map((queryKey) =>
				queryClient.invalidateQueries({ queryKey }),
			),
		);

	const saveBill = useMutation(upsertGasBill, {
		meta: { successMessage: "The bill was saved successfully" },
		onSuccess: invalidate,
		onError: async (mutationError: Error) => {
			await invalidate();
			setError(mutationError.message);
		},
	});

	const removeBill = useMutation(deleteGasBill, {
		meta: { successMessage: "The bill was deleted successfully" },
		onSuccess: invalidate,
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	useEffect(() => {
		if (isError && loadError) {
			setError(loadError.message);
		}
	}, [isError, loadError]);

	const periods = data?.periods ?? [];

	return (
		<>
			<Paper component="section" elevation={2} sx={{ mb: 1, p: 1 }}>
				<Grid2 container spacing={1.5} alignItems="center">
					<Grid2 size={{ xs: 6, sm: 3, md: 1.5 }}>
						<TextField
							select
							fullWidth
							size="small"
							label="Year"
							value={year ?? "all"}
							onChange={(event) =>
								setYear(
									event.target.value === "all"
										? null
										: Number(event.target.value),
								)
							}
						>
							<MenuItem value={"all"}>All</MenuItem>
							{billYears().map((billYear) => (
								<MenuItem key={billYear} value={billYear}>
									{billYear}
								</MenuItem>
							))}
						</TextField>
					</Grid2>
					<Grid2 size={{ xs: 12, md: "auto" }} sx={{ ml: { md: "auto" } }}>
						<Button
							variant="contained"
							size="small"
							startIcon={<Add />}
							data-test-id="new-gas-bill"
							onClick={() => setIsNewBillOpen(true)}
						>
							New bill
						</Button>
					</Grid2>
				</Grid2>
			</Paper>
			<GasBillsTable
				bills={data?.bills ?? []}
				periods={periods}
				isLoading={isFetching || saveBill.isPending || removeBill.isPending}
				onSave={(draft) => saveBill.mutateAsync(draft)}
				onDelete={setBillToDelete}
			/>
			<NewGasBillDialog
				open={isNewBillOpen}
				periods={periods}
				isSaving={saveBill.isPending}
				onClose={() => setIsNewBillOpen(false)}
				onCreate={(period) =>
					saveBill
						.mutateAsync({
							GasBillId: null,
							ServicePeriodStart: period.ServicePeriodStart,
							ServicePeriodEnd: period.ServicePeriodEnd,
							Mcf: null,
							Mmbtu: null,
							Cost: null,
						})
						.then(() => true)
						.catch(() => false)
				}
			/>
			<ConfirmationDialogRaw
				open={billToDelete !== null}
				title="Delete bill"
				message="The bill will be removed, and the days it priced fall back to the bill before it. This cannot be undone."
				onClose={(value?: string) => {
					if (value === "OK" && billToDelete !== null)
						removeBill.mutate(billToDelete);
					setBillToDelete(null);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
